import { cardSurfaceClass } from "@/components/threads/shared.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Card } from "@/components/ui/card.tsx";
import { cn } from "@/lib/utils.ts";
import { formatServiceHost } from "@/utils/url.ts";
import { memo, type ReactNode, useEffect, useRef } from "react";

/** What one connector card shows, normalized so every surface that renders a
 *  tile grid (the Settings gallery, the in-thread connector picker) can treat
 *  first-party providers and dynamic connectors uniformly. */
export type ConnectorTileItem = {
  name: string;
  description?: string;
  websiteUrl?: string;
  icon: ReactNode;
  /** The user has this connector. Stays true even when credentials expired,
   *  so a needs-reauth item doesn't vanish. */
  connected: boolean;
  /** Connected but credentials expired — show "Reconnect" instead of the badge. */
  needsReconnect?: boolean;
  /** Replaces the reconnect line's default wording, for a connector whose fix
   *  is not reconnecting (Text Messages: the person texts Okthx). */
  reconnectNote?: ReactNode;
  /** Overrides the default status badge when provided. Callers that omit it
   *  (the in-thread picker, and the gallery's catalog and WhatsApp tiles) keep
   *  the default Connected badge. Pass `undefined` (not null/false) to keep
   *  the default — any other value replaces it. */
  badge?: ReactNode;
  /** One muted line under the title row, for a fact about this connector the
   *  description does not carry (who can see it, say). */
  note?: ReactNode;
  action: ReactNode;
};

/**
 * One-line reconnect explainer, in the orange the Reconnect action wears.
 * This line, not a badge, carries the reconnect state: a chip beside the
 * Reconnect button squeezed the title out of the header row (the title is
 * the only flex child allowed to shrink, and truncate lets it shrink to
 * zero), and it also keeps the signal on cards that render no action.
 */
const ReconnectNote = ({ message }: { message?: ReactNode }) => (
  <p className="mt-1 text-xs text-amber-700">
    {message ?? "The connection expired. Reconnect to resume syncing."}
  </p>
);

/**
 * The Reconnect action: solid single-color orange, chip-height so it sits in
 * the badge row, matching the note's hue. Shared by every reconnect button so
 * the state has one look wherever it appears.
 *
 * A step darker than the `amber-600` the thread-list status marks wear: white
 * type on 600 measures 3.19:1, under the 4.5:1 contrast bar, where a mark is a
 * shape and owes only 3:1. One hue, darker wherever type sits on it.
 */
export const reconnectButtonClass =
  "h-6 px-2 text-xs shrink-0 border-transparent bg-amber-700 text-white hover:bg-amber-800 hover:text-white";

/** Scrolls a deep-linked connector into view once its highlight arrives. */
const useHighlightScroll = (highlighted?: boolean) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlighted]);
  return ref;
};

/**
 * The default Connected badge, unless the item overrides it. Shared by the tile
 * and the row so the two views cannot disagree about a connector's status.
 *
 * `h-6 py-0` pins the badge to the Connect button's height (size sm's py-1 plus
 * the border runs taller), so badge and button read as one row of chips.
 */
const ConnectorBadges = ({ item }: { item: ConnectorTileItem }) =>
  item.badge !== undefined
    ? item.badge
    : item.connected &&
      !item.needsReconnect && (
        <Badge variant="tertiary" size="sm" className="h-6 py-0 shrink-0">
          Connected
        </Badge>
      );

/**
 * One connector card: icon, name + status badge, description, service host,
 * and the connect action bottom-right. Extracted from the Settings gallery so
 * the in-thread picker renders the identical card — one visual grammar for
 * "here is a service you could connect", wherever it appears.
 *
 * Memoized: the gallery renders one tile per connector and keeps item
 * identities stable across search keystrokes, so memo confines those renders
 * to the tiles that actually changed.
 */
export const ConnectorTile = memo(
  ({ item, highlighted }: { item: ConnectorTileItem; highlighted?: boolean }) => {
    const cardRef = useHighlightScroll(highlighted);
    return (
      <Card
        ref={cardRef}
        // Shadowless, at rest and on hover. A hover shadow is this app's
        // clickable-card signal, and a connector tile is not clickable — only
        // its action button is, so the shadow promised a click that never
        // existed.
        className={cn(cardSurfaceClass, highlighted && "ring-2 ring-primary")}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0">{item.icon}</div>
          <div className="flex-1 min-w-0">
            {/* The action shares the title row, right-aligned — the card stays
              compact enough for two-across grids, and the eye finds name and
              button on one line. */}
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-foreground truncate">{item.name}</h3>
              <ConnectorBadges item={item} />
              <div className="ml-auto shrink-0">{item.action}</div>
            </div>
            {item.needsReconnect && <ReconnectNote message={item.reconnectNote} />}
            {item.note && <p className="mt-1 text-xs text-muted-foreground">{item.note}</p>}
            {item.description && (
              <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{item.description}</p>
            )}
            {item.websiteUrl && (
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {formatServiceHost(item.websiteUrl)}
              </p>
            )}
          </div>
        </div>
      </Card>
    );
  },
);

/**
 * The list form of a connector: the same content on one line, for the gallery's
 * list view. It renders through the same `ConnectorBadges` and surface as the
 * tile, so the two views cannot disagree about a connector's status or shape.
 * Like the tile it is not clickable — only its action is — so it takes the
 * resting surface with no hover treatment.
 */
export const ConnectorRow = memo(
  ({ item, highlighted }: { item: ConnectorTileItem; highlighted?: boolean }) => {
    const rowRef = useHighlightScroll(highlighted);
    return (
      <Card
        ref={rowRef}
        className={cn(
          cardSurfaceClass,
          "flex items-center gap-3",
          highlighted && "ring-2 ring-primary",
        )}
      >
        <div className="shrink-0">{item.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-foreground truncate">{item.name}</h3>
            <ConnectorBadges item={item} />
          </div>
          {item.needsReconnect && <ReconnectNote message={item.reconnectNote} />}
          {item.note && <p className="mt-0.5 text-xs text-muted-foreground">{item.note}</p>}
          {item.description && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{item.description}</p>
          )}
        </div>
        <div className="shrink-0">{item.action}</div>
      </Card>
    );
  },
);
